// Policy di sessione per inattività — contratto condiviso BE↔FE.
//
// SoT dei valori: backend (`resolveSessionPolicy`), che fonde il default di
// piattaforma (`system_settings.session_policy`, modificabile dal master) con
// l'eventuale override per tenant (`tenant_settings`, NULL = eredita).
// I frontend la ricevono in `sessionPolicy` dalla risposta di login /
// refresh-token e la convertono in millisecondi col motore condiviso
// `session-idle` di `@easyflow/ui-frontend` (`sessionPolicyToConfig`); il
// fallback client quando il server non ne manda una vive lì.
//
// Unità sul wire: SECONDI. La UI master ragiona in minuti e converte.

import { z } from 'zod';

/** Limiti di piattaforma (secondi): 5 min ≤ idle ≤ 8 h; preavviso ≥ 1 min. */
export const SESSION_IDLE_TIMEOUT_MIN_SEC = 300;
export const SESSION_IDLE_TIMEOUT_MAX_SEC = 28_800;
export const SESSION_WARN_BEFORE_MIN_SEC = 60;

/** Default hardcoded di piattaforma (backend) quando nessuna policy è persistita. */
export const DEFAULT_SESSION_POLICY = Object.freeze({
  idleTimeoutSec: 900,
  warnBeforeSec: 120,
});

const SESSION_POLICY_WARN_LT_IDLE_MESSAGE =
  'Il preavviso deve essere inferiore al tempo di inattività';

export const sessionPolicySchema = z
  .object({
    /** Secondi di inattività completa dopo i quali la sessione viene chiusa. */
    idleTimeoutSec: z
      .number()
      .int()
      .min(SESSION_IDLE_TIMEOUT_MIN_SEC)
      .max(SESSION_IDLE_TIMEOUT_MAX_SEC),
    /** Secondi di preavviso (popup con countdown) prima della disconnessione. */
    warnBeforeSec: z.number().int().min(SESSION_WARN_BEFORE_MIN_SEC),
    /**
     * Secondi residui dell'access token sotto i quali il client deve rinnovarlo
     * (solo se la sessione non è scaduta per inattività). Opzionale: senza, il
     * client usa il proprio default.
     */
    refreshThresholdSec: z.number().int().positive().optional(),
  })
  .refine((p) => p.warnBeforeSec < p.idleTimeoutSec, {
    message: SESSION_POLICY_WARN_LT_IDLE_MESSAGE,
    path: ['warnBeforeSec'],
  });
export type SessionPolicy = z.infer<typeof sessionPolicySchema>;

/** Body `PUT /api/master/session-policy` (default globale di piattaforma). */
export const sessionPolicyUpdateBodySchema = sessionPolicySchema;
export type SessionPolicyUpdateBody = z.infer<typeof sessionPolicyUpdateBodySchema>;

/**
 * Risposta `GET/PUT /api/master/session-policy`: policy globale effettiva e
 * provenienza (`persisted` = riga in `system_settings`, `default` = hardcoded).
 */
export const sessionPolicyMasterResponseSchema = z.object({
  policy: sessionPolicySchema,
  source: z.enum(['default', 'persisted']),
  updatedAt: z.string().nullable(),
});
export type SessionPolicyMasterResponse = z.infer<typeof sessionPolicyMasterResponseSchema>;

/** Frammento comune alle risposte di login / refresh-token (tenant, master, BFF). */
export const sessionPolicyResponseFragmentSchema = z.object({
  sessionPolicy: sessionPolicySchema,
});
export type SessionPolicyResponseFragment = z.infer<typeof sessionPolicyResponseFragmentSchema>;
